/**
 * 语言判断与切换
 *
 * 约定（对齐 mall-app）：
 *   - 对外 / 存储 / URL 用 LocaleCode：zh_CN / en_US / km_KH（下划线）
 *   - 交给 i18next 的 lng 用 I18nLang：zh-CN / en-US / km-KH（短横）
 *
 * 全站支持：简体中文（zh_CN）、英语（en_US）、柬埔寨语/高棉语（km_KH）。
 * 单页可按需收窄（见 setPageLocales），官网页面目前是唯一启用三语的页面。
 *
 * 优先级（getLang）：
 *   1) URL ?lang=zh_CN|en_US|km_KH
 *   2) localStorage 记忆的用户选择
 *   3) 系统 / 浏览器语言（按 zh / km / en 前缀匹配；页面可关闭，见 detectSystemLang）
 *   4) 页面默认语言（PageLangOptions.defaultLocale，如官网默认柬埔寨语）
 *   5) 运行期默认语言（RuntimeConfig.defaultLocale）
 *   6) 英语（兜底）
 *
 * 每一级都会先按「当前页面支持的语言」过滤，不支持就顺延到下一级；
 * 全部没命中时固定落到英语（页面不支持英语时退到其第一语言）。
 *
 * 注意：只有 URL 与语言切换器属于「用户显式选择」，系统语言和默认值都只是兜底猜测。
 * 因此主投放地区与系统语言分布不一致的页面（如官网面向柬埔寨用户，要求默认柬语）
 * 可以关掉系统语言匹配，仅保留显式选择 + 页面默认语言。
 */
import { getRuntimeConfig } from "@/shared/config/env"

export type LocaleCode = "zh_CN" | "en_US" | "km_KH"
export type I18nLang = "zh-CN" | "en-US" | "km-KH"

const LOCALE_STORAGE_KEY = "mp_locale"

/** 全站语言全集，顺序即下拉展示顺序 */
export const ALL_LOCALES: LocaleCode[] = ["zh_CN", "en_US", "km_KH"]

/** 默认只给落地页中 / 英两语，官网通过 createPage 显式放开三语 */
const DEFAULT_PAGE_LOCALES: LocaleCode[] = ["zh_CN", "en_US"]

const LOCALE_TO_I18N: Record<LocaleCode, I18nLang> = {
  zh_CN: "zh-CN",
  en_US: "en-US",
  km_KH: "km-KH",
}

const I18N_TO_LOCALE: Record<I18nLang, LocaleCode> = {
  "zh-CN": "zh_CN",
  "en-US": "en_US",
  "km-KH": "km_KH",
}

/** <html lang> 用 BCP-47 短标记 */
const LOCALE_TO_HTML: Record<LocaleCode, string> = {
  zh_CN: "zh-CN",
  en_US: "en-US",
  km_KH: "km",
}

const LOCALES = new Set<string>(ALL_LOCALES)

let pageLocales: LocaleCode[] = DEFAULT_PAGE_LOCALES

/** 页面级默认语言（未设置时走运行期默认语言） */
let pageDefaultLocale: LocaleCode | null = null

/** 是否按系统 / 浏览器语言自动匹配，默认开启 */
let pageDetectSystemLang = true

export interface PageLangOptions {
  /**
   * 本页默认语言。必须在本页支持的语言范围内，否则忽略。
   * 只影响「没有任何显式选择」时的兜底结果，不会覆盖 URL 与用户记忆。
   */
  defaultLocale?: LocaleCode
  /** 是否按系统 / 浏览器语言自动匹配；传 false 时页面固定用默认语言。默认 true */
  detectSystemLang?: boolean
}

export function localeToI18n(code: LocaleCode): I18nLang {
  return LOCALE_TO_I18N[code]
}

export function i18nToLocale(lang: I18nLang): LocaleCode {
  return I18N_TO_LOCALE[lang]
}

export function localeToHtmlLang(code: LocaleCode): string {
  return LOCALE_TO_HTML[code]
}

function isLocaleCode(value: string | null): value is LocaleCode {
  return !!value && LOCALES.has(value)
}

/** 当前页面支持的语言（未设置时为中 / 英） */
export function getPageLocales(): LocaleCode[] {
  return pageLocales
}

/**
 * 声明当前页面支持的语言与语言兜底策略。由 createPage 在挂载前调用一次。
 * 传入空数组 / undefined 时回到默认（中 / 英）。
 *
 * options 不传时保持既有行为：无页面默认语言、启用系统语言匹配。
 */
export function setPageLocales(
  locales?: LocaleCode[],
  options: PageLangOptions = {},
): void {
  const next = (locales ?? []).filter((code) => LOCALES.has(code))
  pageLocales = next.length ? next : DEFAULT_PAGE_LOCALES

  // 页面默认语言必须在本页语言范围内，否则视为未配置（交给运行期默认语言）
  const defaultLocale = options.defaultLocale
  pageDefaultLocale =
    defaultLocale && pageLocales.includes(defaultLocale) ? defaultLocale : null

  pageDetectSystemLang = options.detectSystemLang !== false
}

/** 命中页面语言范围则返回，否则返回 null（交给下一优先级） */
function pick(detected: LocaleCode | null): LocaleCode | null {
  return detected && pageLocales.includes(detected) ? detected : null
}

function getUrlLang(): LocaleCode | null {
  if (typeof window === "undefined") return null
  const param = new URLSearchParams(window.location.search).get("lang")
  return isLocaleCode(param) ? param : null
}

function getStoredLang(): LocaleCode | null {
  if (typeof window === "undefined") return null
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocaleCode(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * 系统 / 浏览器语言匹配：按 navigator.languages 顺序找第一个能识别的。
 * 命中 zh* → 中文，km* → 柬埔寨语，en* → 英语；都不命中返回 null（由调用方兜底英语）。
 */
function getSystemLang(): LocaleCode | null {
  if (typeof navigator === "undefined") return null

  const list = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]

  for (const raw of list) {
    const lang = (raw || "").toLowerCase()
    if (lang.startsWith("zh")) return "zh_CN"
    if (lang.startsWith("km")) return "km_KH"
    if (lang.startsWith("en")) return "en_US"
  }

  return null
}

/** 运行期默认语言（构建期注入，可被 window.__MP_CONFIG__ 覆盖） */
function getRuntimeDefaultLang(): LocaleCode | null {
  const raw = getRuntimeConfig().defaultLocale
  return raw in I18N_TO_LOCALE ? I18N_TO_LOCALE[raw as I18nLang] : null
}

/** 兜底：页面默认语言优先；其次英语；最后取页面第一语言 */
function fallbackLang(): LocaleCode {
  if (pageDefaultLocale) return pageDefaultLocale
  return pageLocales.includes("en_US") ? "en_US" : pageLocales[0]
}

/**
 * 当前语言。
 *
 * 页面默认语言（defaultLocale）比运行期默认语言更具体，因此在两者都命中时
 * 页面默认语言优先——官网就是靠这一点固定为柬埔寨语。
 */
export function getLang(): LocaleCode {
  const candidates: (LocaleCode | null)[] = [
    getUrlLang(),
    getStoredLang(),
    // 页面关闭系统语言匹配时，整级跳过（不参与过滤，直接顺延到默认语言）
    pageDetectSystemLang ? getSystemLang() : null,
    pageDefaultLocale ?? getRuntimeDefaultLang(),
  ]

  for (const candidate of candidates) {
    const hit = pick(candidate)
    if (hit) return hit
  }

  return fallbackLang()
}

/** 切换语言：记忆到 localStorage，并修正 <html lang> */
export function setLocale(code: LocaleCode): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, code)
    } catch {
      /* localStorage 不可用时忽略 */
    }
    document.documentElement.lang = localeToHtmlLang(code)
  }
}
